// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import { signOutEverywhere } from '../auth/identity/signOut';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Box,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  TrendingUp,
  Assessment,
  PersonalVideo,
  Close,
  Receipt,
  Analytics,
  Settings,
  AdminPanelSettings,
  ExpandMore,
  ExpandLess,
  ShowChart,
  Calculate,
  ListAlt,
  AccountTree,
  History,
  Timeline,
  DynamicFeed,
  School,
  DeviceHub,
  WbSunnyOutlined,
  CandlestickChart,
  FactCheckOutlined,
  TrackChanges,
  Search as SearchIcon,
  Check,
  PersonAddAlt1,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  listAccounts,
  switchAccount,
  signOutActiveAccount,
  snapshotActiveAccount,
  clearAll,
} from '../features/account/accountRegistry';
import { fetchStrategies } from '../features/visualStrategies/visualStrategiesSlice';
import usePermissions from '../hooks/usePermissions';
import useNavTier from '../hooks/useNavTier';
import useVersionCheck from '../hooks/useVersionCheck';
import UpdateAvailableIndicator from './UpdateAvailableIndicator';
import ThemeModeToggle from './ThemeModeToggle';
import CommandPalette, { useCommandPaletteHotkey } from './CommandPalette';
import { getLastChartPath, isChartPath } from '../utils/lastChart';
import MobileTabBar, { tabIcons } from './MobileTabBar';

// ============================================================================
// DWLF Navbar — Brutalist Design
// ============================================================================

const DropdownNavButton = ({ menu, isActive, isActivePath }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        aria-controls={isOpen ? `${menu.id}-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        onClick={handleClick}
        endIcon={<ExpandMore sx={{
          transition: 'transform 0.2s',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          fontSize: '1rem',
        }} />}
        startIcon={menu.icon}
        sx={{
          fontFamily: '"Space Mono", monospace',
          fontSize: '0.75rem',
          fontWeight: 500,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: isActive ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-60)',
          backgroundColor: 'transparent',
          borderRadius: 0,
          padding: '10px 16px',
          margin: '0 2px',
          position: 'relative',
          transition: 'color 0.2s ease',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'var(--dwlf-accent)',
          },
          '&::after': isActive ? {
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '16px',
            right: '16px',
            height: '2px',
            backgroundColor: 'var(--dwlf-accent)',
          } : {},
        }}
      >
        {menu.label}
      </Button>
      <Menu
        id={`${menu.id}-menu`}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ sx: { py: 1 } }}
        PaperProps={{
          sx: {
            mt: 0,
            minWidth: '200px',
          },
        }}
      >
        {menu.children.map((child) => {
          const childActive = isActivePath(child.path, child.exactMatch);
          return (
            <MenuItem
              key={child.path}
              component={Link}
              to={child.path}
              sx={{
                mx: 1,
                my: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                backgroundColor: childActive ? 'var(--dwlf-accent)' : 'transparent',
                color: childActive ? 'var(--dwlf-bg)' : 'var(--dwlf-ink-70)',
                '&:hover': {
                  backgroundColor: childActive ? 'var(--dwlf-accent)' : 'var(--dwlf-accent-10)',
                  color: childActive ? 'var(--dwlf-bg)' : 'var(--dwlf-accent)',
                },
              }}
            >
              {child.icon}
              <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8rem' }}>
                {child.label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const user = useSelector((state) => state.account.user);
  const strategies = useSelector((state) => state.visualStrategies.strategies);
  const { isAdmin, hasPermission, loaded: permissionsLoaded } = usePermissions();
  useNavTier(); // Hook still runs (manages tier state) but nav no longer gates on tier
  const { updateAvailable, refresh: refreshForUpdate } = useVersionCheck();
  const canAccessAdmin = isAdmin() || hasPermission('roles:write');
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteHotkey(setPaletteOpen, !!user);
  // Signed out (e.g. a session expiring while it was open): close it, or it reappears after login.
  useEffect(() => { if (!user) setPaletteOpen(false); }, [user]);
  const [mobileExpandedSection, setMobileExpandedSection] = useState(null);

  // Fetch strategies when user logs in to check for onboarding
  useEffect(() => {
    if (user) {
      dispatch(fetchStrategies());
    }
  }, [user, dispatch]);

  // Check if user has no strategies (for reordering nav)
  const strategiesLoading = useSelector((state) => state.visualStrategies.loading);
  const hasNoStrategies = !strategiesLoading && user && strategies.length === 0;

  const handleMenuOpen = (event) => {
    // Persist + freshen the active account in the registry when the menu opens,
    // so the switcher lists it and a later switch restores its current tokens.
    snapshotActiveAccount();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    // Persist + freshen the active account when opening the drawer (mirrors the
    // desktop menu) so the switcher lists it and a later switch restores tokens.
    if (!mobileMenuOpen) snapshotActiveAccount();
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    signOutEverywhere(dispatch);
    handleMenuClose();
    setMobileMenuOpen(false);
    navigate('/login');
  };

  // Multi-account switcher actions.
  const handleAddAccount = () => {
    snapshotActiveAccount();
    handleMenuClose();
    setMobileMenuOpen(false);
    navigate('/login?addAccount=1');
  };

  const handleSwitchAccount = (accountId) => {
    handleMenuClose();
    switchAccount(accountId); // hard-reloads as the target account
  };

  const handleSignOut = () => {
    // With multiple accounts, sign out the active one and fall back to another.
    // With one account, fall through to the existing full logout (clears the
    // registry too so a fresh login starts clean).
    if (listAccounts().length > 1) {
      handleMenuClose();
      signOutActiveAccount(); // hard-reloads
      return;
    }
    clearAll();
    handleLogout();
  };

  const handleSignOutAll = () => {
    clearAll();
    handleLogout();
  };

  // Accounts for the mobile drawer switcher (computed only while open; pure read).
  const mobileAccounts = mobileMenuOpen ? listAccounts() : [];

  const isActivePath = (path, exactMatch = false) => {
    if (exactMatch) {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isDropdownActive = (children, ownsPrefixes = []) => (
    ownsPrefixes.some((prefix) => isActivePath(prefix))
    || children.some((child) => isActivePath(child.path, child.exactMatch))
  );

  const handleMobileAccordionToggle = (sectionId) => {
    setMobileExpandedSection(prev => prev === sectionId ? null : sectionId);
  };

  // Redesign IA (DWLF-299): the decision loop first, then the workbench.
  //   Today · Markets · Chart · Log  |  Signals · Build
  // Today = the decision queue, Markets = where every market sits, Chart = the desk for one symbol
  // (reopens the last one charted), Log = what was decided and how it went. Signals and Build hold
  // what the old Events / Strategies menus did; every previous route is still reachable here or via ⌘K.
  // On a chart page the current one is the last chart; the page records it only after this render.
  const lastChartPath = isChartPath(location.pathname) ? location.pathname : getLastChartPath();
  const standaloneItems = user ? [
    {
      label: 'Today',
      path: '/today',
      icon: <WbSunnyOutlined sx={{ fontSize: '1rem' }} />,
      requiresAuth: true,
    },
  ] : [];

  const chartItem = {
    label: 'Chart',
    path: lastChartPath,
    icon: <CandlestickChart sx={{ fontSize: '1rem' }} />,
    requiresAuth: true,
    isActive: isChartPath,
  };

  const dropdownMenus = [
    {
      id: 'markets',
      label: 'Markets',
      icon: <TrendingUp sx={{ fontSize: '1rem' }} />,
      requiresAuth: false,
      children: [
        { label: 'Cycle Clock', path: '/markets', icon: <TrackChanges fontSize="small" />, exactMatch: true },
        { label: 'Overview', path: '/markets/overview', icon: <TrendingUp fontSize="small" /> },
        { label: 'Cycle Screener', path: '/screener', icon: <Timeline fontSize="small" />, requiresAuth: true },
        { label: 'COT (Silver)', path: '/markets/cot', icon: <ShowChart fontSize="small" /> },
      ],
    },
    {
      id: 'log',
      label: 'Log',
      icon: <ListAlt sx={{ fontSize: '1rem' }} />,
      requiresAuth: true,
      // Every trade page (detail, review, new, counterfactual…) belongs to Log, not just its menu items.
      ownsPrefixes: ['/trades', '/trade-plans'],
      children: [
        { label: 'Trade Journal', path: '/trades', icon: <ListAlt fontSize="small" />, exactMatch: true },
        { label: 'Decision Quality', path: '/trades/decisions', icon: <FactCheckOutlined fontSize="small" /> },
        { label: 'Skips · Counterfactual', path: '/trades/counterfactual', icon: <History fontSize="small" /> },
        { label: 'Trade Plans', path: '/trade-plans', icon: <Assessment fontSize="small" /> },
        { label: 'Analytics', path: '/trades/analytics', icon: <Analytics fontSize="small" /> },
        { label: 'Position Size', path: '/tools/position-size', icon: <Calculate fontSize="small" /> },
      ],
    },
    {
      id: 'signals',
      label: 'Signals',
      icon: <DynamicFeed sx={{ fontSize: '1rem' }} />,
      requiresAuth: true,
      groupStart: true,
      children: [
        { label: 'Strategy Signals', path: '/my-signals', icon: <PersonalVideo fontSize="small" /> },
        { label: 'Events Explorer', path: '/signals/events', icon: <Timeline fontSize="small" /> },
        { label: 'Activity Feed', path: '/activity', icon: <DynamicFeed fontSize="small" /> },
        { label: 'Alert Log', path: '/alerts/log', icon: <DynamicFeed fontSize="small" />, requiresAuth: true },
      ],
    },
    {
      id: 'build',
      label: 'Build',
      icon: <AccountTree sx={{ fontSize: '1rem' }} />,
      requiresAuth: true,
      children: [
        { label: 'Custom Events', path: '/custom-events', icon: <ShowChart fontSize="small" /> },
        { label: 'Strategies', path: '/strategies', icon: <AccountTree fontSize="small" /> },
        { label: 'Backtests', path: '/backtests', icon: <History fontSize="small" /> },
        { label: 'State Machines', path: '/state-machines', icon: <DeviceHub fontSize="small" /> },
      ],
    },
  ];

  const NavButton = ({ item, mobile = false }) => {
    const isActive = item.isActive ? item.isActive(location.pathname) : isActivePath(item.path);

    if (mobile) {
      return (
        <ListItem
          button
          component={Link}
          to={item.path}
          onClick={() => setMobileMenuOpen(false)}
          sx={{
            margin: '4px 0',
            backgroundColor: isActive ? 'var(--dwlf-accent)' : 'transparent',
            color: isActive ? 'var(--dwlf-bg)' : 'var(--dwlf-ink-70)',
            '&:hover': {
              backgroundColor: isActive ? 'var(--dwlf-accent)' : 'var(--dwlf-accent-10)',
              color: isActive ? 'var(--dwlf-bg)' : 'var(--dwlf-accent)',
            },
          }}
        >
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
            {item.icon}
          </Box>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              sx: { fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }
            }}
          />
        </ListItem>
      );
    }

    return (
      <Button
        component={Link}
        to={item.path}
        startIcon={item.icon}
        sx={{
          fontFamily: '"Space Mono", monospace',
          fontSize: '0.75rem',
          fontWeight: 500,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: isActive ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-60)',
          backgroundColor: 'transparent',
          borderRadius: 0,
          padding: '10px 16px',
          margin: '0 2px',
          position: 'relative',
          transition: 'color 0.2s ease',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'var(--dwlf-accent)',
          },
          '&::after': isActive ? {
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '16px',
            right: '16px',
            height: '2px',
            backgroundColor: 'var(--dwlf-accent)',
          } : {},
        }}
      >
        {item.label}
      </Button>
    );
  };

  const MobileAccordionSection = ({ menu }) => {
    const isExpanded = mobileExpandedSection === menu.id;
    const isActive = isDropdownActive(menu.children, menu.ownsPrefixes);

    return (
      <Box>
        <ListItem
          button
          onClick={() => handleMobileAccordionToggle(menu.id)}
          sx={{
            margin: '4px 0',
            backgroundColor: isActive && !isExpanded ? 'var(--dwlf-accent-10)' : 'transparent',
            '&:hover': {
              backgroundColor: 'var(--dwlf-accent-08)',
            },
          }}
        >
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', color: isActive ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-70)' }}>
            {menu.icon}
          </Box>
          <ListItemText
            primary={menu.label}
            primaryTypographyProps={{
              sx: {
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.85rem',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-70)',
              }
            }}
          />
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {menu.children.map((child) => {
              const childActive = isActivePath(child.path, child.exactMatch);
              return (
                <ListItem
                  key={child.path}
                  button
                  component={Link}
                  to={child.path}
                  onClick={() => setMobileMenuOpen(false)}
                  sx={{
                    margin: '2px 0',
                    backgroundColor: childActive ? 'var(--dwlf-accent)' : 'transparent',
                    color: childActive ? 'var(--dwlf-bg)' : 'var(--dwlf-ink-60)',
                    '&:hover': {
                      backgroundColor: childActive ? 'var(--dwlf-accent)' : 'var(--dwlf-accent-08)',
                      color: childActive ? 'var(--dwlf-bg)' : 'var(--dwlf-accent)',
                    },
                  }}
                >
                  <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    {child.icon}
                  </Box>
                  <ListItemText
                    primary={child.label}
                    primaryTypographyProps={{
                      sx: { fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8rem' }
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </Box>
    );
  };

  const UserMenu = () => {
    const accounts = anchorEl ? listAccounts() : [];
    return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        sx={{
          padding: '8px',
          borderRadius: 0,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'var(--dwlf-accent-10)',
          },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            backgroundColor: 'var(--dwlf-accent)',
            color: 'var(--dwlf-bg)',
            fontSize: '0.8rem',
            fontWeight: 600,
            fontFamily: '"Space Mono", monospace',
            borderRadius: 0,
          }}
        >
          {user?.email?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{ onMouseLeave: handleMenuClose }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: '200px',
            left: 'unset !important',
            right: 16,
          }
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'var(--dwlf-ink-50)',
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.05em',
            }}
          >
            SIGNED IN AS
          </Typography>
        </Box>
        {accounts.map((acct) => (
          <MenuItem
            key={acct.accountId}
            onClick={acct.isActive ? undefined : () => handleSwitchAccount(acct.accountId)}
            disableRipple={acct.isActive}
            sx={{
              margin: '2px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: acct.isActive ? 'default' : 'pointer',
            }}
          >
            <Check
              fontSize="small"
              sx={{ visibility: acct.isActive ? 'visible' : 'hidden', color: 'var(--dwlf-accent)' }}
            />
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.85rem',
                fontWeight: acct.isActive ? 600 : 400,
              }}
            >
              {acct.email}
            </Typography>
          </MenuItem>
        ))}
        <MenuItem
          onClick={handleAddAccount}
          sx={{ margin: '2px 8px', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <PersonAddAlt1 fontSize="small" />
          <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }}>
            Add account
          </Typography>
        </MenuItem>
        <Divider />
        {canAccessAdmin && (
          <MenuItem
            component={Link}
            to="/admin"
            onClick={() => {
              handleMenuClose();
              setMobileMenuOpen(false);
            }}
            sx={{
              margin: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AdminPanelSettings fontSize="small" />
            <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }}>
              Admin Tools
            </Typography>
          </MenuItem>
        )}
        {/* Skip Learn in avatar dropdown when hasNoStrategies — it's
            already promoted as a NavButton in the main nav bar */}
        {!hasNoStrategies && (
          <MenuItem
            component={Link}
            to="/learn"
            onClick={() => {
              handleMenuClose();
              setMobileMenuOpen(false);
            }}
            sx={{
              margin: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <School fontSize="small" />
            <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }}>
              Learn
            </Typography>
          </MenuItem>
        )}
        {/* Only show divider when at least one item above rendered,
            otherwise we'd get a double divider after the email */}
        {(canAccessAdmin || !hasNoStrategies) && (
          <Divider />
        )}
        <MenuItem
          component={Link}
          to="/settings"
          onClick={() => {
            handleMenuClose();
            setMobileMenuOpen(false);
          }}
          sx={{
            margin: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Settings fontSize="small" />
          <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }}>
            Settings
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleSignOut}
          sx={{
            margin: '4px 8px',
            color: 'var(--dwlf-down)',
            '&:hover': {
              backgroundColor: 'var(--dwlf-down-10)',
            },
          }}
        >
          <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }}>
            {accounts.length > 1 ? `Sign out ${accounts.find((a) => a.isActive)?.email || ''}`.trim() : 'Sign out'}
          </Typography>
        </MenuItem>
        {accounts.length > 1 && (
          <MenuItem
            onClick={handleSignOutAll}
            sx={{
              margin: '4px 8px',
              color: 'var(--dwlf-down)',
              '&:hover': {
                backgroundColor: 'var(--dwlf-down-10)',
              },
            }}
          >
            <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }}>
              Sign out of all accounts
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
    );
  };

  const AuthButtons = () => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        component={Link}
        to="/login"
        sx={{
          fontFamily: '"Space Mono", monospace',
          fontSize: '0.75rem',
          fontWeight: 500,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--dwlf-bg)',
          backgroundColor: 'var(--dwlf-accent)',
          border: 'none',
          borderRadius: 0,
          px: 3,
          py: 1,
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: 'var(--dwlf-text)',
          },
        }}
      >
        Login
      </Button>
    </Box>
  );

  return (
    <>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ minHeight: '64px', px: { xs: 2, md: 4 } }}>
          {/* Logo */}
          <Typography
            component={Link}
            to="/"
            sx={{
              flexGrow: isMobile ? 1 : 0,
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--dwlf-accent)',
              textDecoration: 'none',
              marginRight: 6,
              letterSpacing: '-0.02em',
              transition: 'opacity 0.2s ease',
              '&:hover': {
                opacity: 0.8,
              }
            }}
          >
            DWLF
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              {standaloneItems
                .filter(item =>
                  (!item.requiresAuth || user) &&
                  (!item.requiresPermission || (permissionsLoaded && hasPermission(item.requiresPermission)))
                )
                .map((item) => (
                  <NavButton key={item.path} item={item} />
                ))
              }

              {/* Show Learn in main nav for unauthenticated visitors
                  (it was requiresAuth: false before the restructure)
                  and for new users without strategies */}
              {(!user || hasNoStrategies) && (
                <NavButton
                  key="learn"
                  item={{
                    label: 'Learn',
                    path: '/learn',
                    icon: <School sx={{ fontSize: '1rem' }} />,
                    requiresAuth: false
                  }}
                />
              )}

              {dropdownMenus
                .filter(menu =>
                  (!menu.requiresAuth || user) &&
                  (!menu.requiresPermission || (permissionsLoaded && hasPermission(menu.requiresPermission)))
                )
                .map((menu) => (
                  <React.Fragment key={menu.id}>
                    {menu.groupStart && (
                      <Box aria-hidden sx={{ width: '1px', height: 20, mx: 1.5, backgroundColor: 'var(--dwlf-ink-12)' }} />
                    )}
                    <DropdownNavButton
                      menu={menu}
                      isActive={isDropdownActive(menu.children, menu.ownsPrefixes)}
                      isActivePath={isActivePath}
                    />
                    {menu.id === 'markets' && user && <NavButton item={chartItem} />}
                  </React.Fragment>
                ))
              }

            </Box>
          )}

          {/* Desktop User Menu / Auth Buttons */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <UpdateAvailableIndicator updateAvailable={updateAvailable} onRefresh={refreshForUpdate} />
              {user && (
                <Button
                  onClick={() => setPaletteOpen(true)}
                  aria-label="Search markets, pages, strategies and events"
                  startIcon={<SearchIcon sx={{ fontSize: '1rem !important' }} />}
                  sx={{
                    height: 34, px: 1.5, minWidth: 0, gap: 1, borderRadius: 0, textTransform: 'none',
                    border: '1px solid var(--dwlf-ink-12)', backgroundColor: 'var(--dwlf-surface)',
                    color: 'var(--dwlf-ink-55)', fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.75rem',
                    '&:hover': { borderColor: 'var(--dwlf-accent)', backgroundColor: 'var(--dwlf-surface)' },
                  }}
                >
                  Jump to…
                  <Box component="span" sx={{ ml: 1, px: 0.75, border: '1px solid var(--dwlf-ink-12)', fontFamily: '"Space Mono", monospace', fontSize: '0.65rem' }}>⌘K</Box>
                </Button>
              )}
              <ThemeModeToggle />
              {user ? <UserMenu /> : <AuthButtons />}
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && user && (
            <IconButton
              onClick={() => setPaletteOpen(true)}
              aria-label="Search markets, pages, strategies and events"
              sx={{ color: 'var(--dwlf-ink-80)', borderRadius: 0 }}
            >
              <SearchIcon />
            </IconButton>
          )}
          {isMobile && (
            <IconButton
              onClick={handleMobileMenuToggle}
              aria-label="Open menu"
              sx={{
                color: 'var(--dwlf-ink-80)',
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: 'var(--dwlf-accent-10)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      {user && <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />}
      {isMobile && user && (
        <MobileTabBar
          onMore={handleMobileMenuToggle}
          tabs={[
            { label: 'Today', path: '/today', icon: tabIcons.today, active: isActivePath('/today') },
            { label: 'Markets', path: '/markets', icon: tabIcons.markets, active: (location.pathname.startsWith('/markets') && !isChartPath(location.pathname)) || isActivePath('/screener') },
            { label: 'Chart', path: lastChartPath, icon: tabIcons.chart, active: isChartPath(location.pathname) },
            { label: 'Log', path: '/trades', icon: tabIcons.log, active: ['/trades', '/trade-plans'].some((p) => isActivePath(p)) },
          ]}
        />
      )}

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: '280px',
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              letterSpacing: '-0.01em',
            }}
          >
            Menu
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <UpdateAvailableIndicator updateAvailable={updateAvailable} onRefresh={refreshForUpdate} />
            <ThemeModeToggle />
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              sx={{ borderRadius: 0 }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
        <Divider />

        {user && (
          <>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    backgroundColor: 'var(--dwlf-accent)',
                    color: 'var(--dwlf-bg)',
                    fontFamily: '"Space Mono", monospace',
                    borderRadius: 0,
                  }}
                >
                  {user?.email?.charAt(0).toUpperCase()}
                </Avatar>
                <Typography
                  sx={{
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: '0.7rem',
                    color: 'var(--dwlf-ink-50)',
                    letterSpacing: '0.05em',
                  }}
                >
                  SIGNED IN AS
                </Typography>
              </Box>
              <Box sx={{ mt: 1 }}>
                {mobileAccounts.map((acct) => (
                  <MenuItem
                    key={acct.accountId}
                    onClick={acct.isActive ? undefined : () => handleSwitchAccount(acct.accountId)}
                    sx={{ px: 1, py: 0.75, gap: 1, cursor: acct.isActive ? 'default' : 'pointer' }}
                  >
                    <Check
                      fontSize="small"
                      sx={{ visibility: acct.isActive ? 'visible' : 'hidden', color: 'var(--dwlf-accent)' }}
                    />
                    <Typography
                      sx={{
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontSize: '0.85rem',
                        fontWeight: acct.isActive ? 600 : 400,
                      }}
                    >
                      {acct.email}
                    </Typography>
                  </MenuItem>
                ))}
                <MenuItem onClick={handleAddAccount} sx={{ px: 1, py: 0.75, gap: 1 }}>
                  <PersonAddAlt1 fontSize="small" />
                  <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }}>
                    Add account
                  </Typography>
                </MenuItem>
              </Box>
            </Box>
            <Divider />
          </>
        )}

        <List sx={{ flexGrow: 1, px: 2 }}>
          {user && canAccessAdmin && (
            <ListItem
              button
              component={Link}
              to="/admin"
              onClick={() => setMobileMenuOpen(false)}
              sx={{
                margin: '4px 0',
                backgroundColor: isActivePath('/admin') ? 'var(--dwlf-accent)' : 'transparent',
                color: isActivePath('/admin') ? 'var(--dwlf-bg)' : 'var(--dwlf-ink-70)',
                '&:hover': {
                  backgroundColor: isActivePath('/admin') ? 'var(--dwlf-accent)' : 'var(--dwlf-accent-10)',
                },
              }}
            >
              <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                <AdminPanelSettings />
              </Box>
              <ListItemText
                primary="Admin Tools"
                primaryTypographyProps={{
                  sx: { fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }
                }}
              />
            </ListItem>
          )}

          {standaloneItems
            .filter(item =>
              (!item.requiresAuth || user) &&
              (!item.requiresPermission || (permissionsLoaded && hasPermission(item.requiresPermission)))
            )
            .map((item) => (
              <NavButton key={item.path} item={item} mobile />
            ))
          }

          {/* Show Learn second for users without strategies on mobile */}
          {hasNoStrategies && (
            <NavButton
              key="learn"
              item={{
                label: 'Learn',
                path: '/learn',
                icon: <School sx={{ fontSize: '1rem' }} />,
                requiresAuth: false
              }}
              mobile
            />
          )}

          {dropdownMenus
            .filter(menu =>
              (!menu.requiresAuth || user) &&
              (!menu.requiresPermission || (permissionsLoaded && hasPermission(menu.requiresPermission)))
            )
            .map((menu) => (
              <React.Fragment key={menu.id}>
                {menu.groupStart && <Divider sx={{ my: 1 }} />}
                <MobileAccordionSection menu={menu} />
                {menu.id === 'markets' && user && <NavButton item={chartItem} mobile />}
              </React.Fragment>
            ))
          }

          {/* Items that live in the avatar dropdown on desktop but
              need direct links in the mobile drawer. Only shown
              when logged in — matches the desktop UserMenu guard. */}
          {user && (
            <>
              <Divider sx={{ my: 1 }} />
              {/* Skip Learn here when hasNoStrategies — it's already
                  rendered in the promoted position above */}
              {!hasNoStrategies && (
                <NavButton
                  item={{
                    label: 'Learn',
                    path: '/learn',
                    icon: <School sx={{ fontSize: '1rem' }} />,
                  }}
                  mobile
                />
              )}
              <NavButton
                item={{
                  label: 'Settings',
                  path: '/settings',
                  icon: <Settings sx={{ fontSize: '1rem' }} />,
                }}
                mobile
              />
            </>
          )}
        </List>

        <Box sx={{ p: 2 }}>
          {user ? (
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleSignOut}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--dwlf-down)',
                  borderColor: 'var(--dwlf-down)',
                  borderRadius: 0,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'var(--dwlf-down-10)',
                    borderColor: 'var(--dwlf-down)',
                  },
                }}
              >
                {mobileAccounts.length > 1
                  ? `Sign out ${mobileAccounts.find((a) => a.isActive)?.email || ''}`.trim()
                  : 'Sign out'}
              </Button>
              {mobileAccounts.length > 1 && (
                <Button
                  fullWidth
                  variant="text"
                  onClick={handleSignOutAll}
                  sx={{
                    mt: 1,
                    fontFamily: '"Space Mono", monospace',
                    fontSize: '0.7rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--dwlf-down-80)',
                    borderRadius: 0,
                    '&:hover': { backgroundColor: 'var(--dwlf-down-10)' },
                  }}
                >
                  Sign out of all accounts
                </Button>
              )}
            </>
          ) : (
            <Button
              fullWidth
              component={Link}
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--dwlf-bg)',
                backgroundColor: 'var(--dwlf-accent)',
                borderRadius: 0,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'var(--dwlf-text)',
                },
              }}
            >
              Login
            </Button>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
